import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type {
    CardPaymentStatusResponse,
    CheckoutData,
    ContractDataComplexDTO,
    FinancialDetailsRequestV2,
    OfferValidation,
    PaymentData,
    PaymentPlan,
    PersonalDetailsClientDTO,
} from '@leanpay/models'
import { MaturityType } from '@leanpay/models'

interface AppState {
    vendorToken: string
    isAuthenticated: boolean
    progress: number
    financialDetailsDataV2: FinancialDetailsRequestV2 | null
    offerValidationData: OfferValidation | null
    personalDetailsData: PersonalDetailsClientDTO | null
    selectedPaymentPlan: PaymentPlan | null
    paymentData: PaymentData | null
    cardPaymentStatusData: CardPaymentStatusResponse | null
    checkoutData: CheckoutData | null
    contractData: ContractDataComplexDTO[] | null
    maturityType: MaturityType
    lastRecordedRoute: string
    isFirstTimeShopper: boolean | null

    setVendorToken: (token: string) => void
    setIsAuthenticated: (auth: boolean) => void
    setProgress: (value: number) => void
    setFinancialDetailsDataV2: (data: FinancialDetailsRequestV2 | null) => void
    setOfferValidationData: (data: OfferValidation | null) => void
    setPersonalDetailsData: (data: PersonalDetailsClientDTO | null) => void
    setSelectedPaymentPlan: (data: PaymentPlan | null) => void
    setPaymentData: (data: PaymentData | null) => void
    setCardPaymentStatusData: (data: CardPaymentStatusResponse | null) => void
    setCheckoutData: (data: CheckoutData | null) => void
    setContractData: (data: ContractDataComplexDTO[] | null) => void
    setMaturityType: (type: MaturityType) => void
    setLastRecordedRoute: (route: string) => void
    setIsFirstTimeShopper: (isFirstTime: boolean | null) => void
    canAccessRoute: (route: string) => boolean
    resetStore: () => void
}

export const useAppStore = create<AppState>()(
    persist(
        (set, get) => ({
            vendorToken: '',
            isAuthenticated: false,
            progress: 0,
            financialDetailsDataV2: null,
            offerValidationData: null,
            personalDetailsData: null,
            selectedPaymentPlan: null,
            paymentData: null,
            cardPaymentStatusData: null,
            checkoutData: null,
            contractData: null,
            maturityType: MaturityType.PRICE,
            lastRecordedRoute: '/',
            isFirstTimeShopper: null,

            setVendorToken: (token) => set({ vendorToken: token }),
            setIsAuthenticated: (auth) => set({ isAuthenticated: auth }),
            setProgress: (value) => set({ progress: value }),
            setFinancialDetailsDataV2: (data) => set({ financialDetailsDataV2: data }),
            setOfferValidationData: (data) => set({ offerValidationData: data }),
            setPersonalDetailsData: (data) => set({ personalDetailsData: data }),
            setSelectedPaymentPlan: (data) => set({ selectedPaymentPlan: data }),
            setPaymentData: (data) => set({ paymentData: data }),
            setCardPaymentStatusData: (data) => set({ cardPaymentStatusData: data }),
            setCheckoutData: (data) => set({ checkoutData: data }),
            setContractData: (data) => set({ contractData: data }),
            setMaturityType: (type) => set({ maturityType: type }),
            setLastRecordedRoute: (route) => set({ lastRecordedRoute: route }),
            setIsFirstTimeShopper: (isFirstTime) => set({ isFirstTimeShopper: isFirstTime }),
            canAccessRoute: (route) => {
                const { lastRecordedRoute } = get()
                // User can only access their last recorded route (for refresh)
                return route === lastRecordedRoute
            },
            resetStore: () =>
                set({
                    vendorToken: '',
                    isAuthenticated: false,
                    progress: 0,
                    financialDetailsDataV2: null,
                    offerValidationData: null,
                    personalDetailsData: null,
                    selectedPaymentPlan: null,
                    paymentData: null,
                    cardPaymentStatusData: null,
                    checkoutData: null,
                    contractData: null,
                    maturityType: MaturityType.PRICE,
                    lastRecordedRoute: '/',
                    isFirstTimeShopper: null,
                }),
        }),
        {
            name: 'leanpay-checkout-session',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
)
